import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Typography,
  Popconfirm,
  Select,
  Row,
  Col,
  InputNumber,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined, StarOutlined } from '@ant-design/icons';
import { FACTORY_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';
import debounce from 'lodash/debounce';
import ActionButtons from '../components/common/ActionButtons';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const FactoryManagement = () => {
  const [data, setData] = useState({ items: [], total: 0, currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    status: undefined
  });

  // State để trigger fetch lại data
  const [shouldRefetch, setShouldRefetch] = useState(0);

  // Lấy danh sách nhà xưởng với phân trang và tìm kiếm
  const fetchFactories = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== undefined && queryParams[key] !== '') {
          params.append(key, queryParams[key]);
        }
      });
      
      const response = await axiosInstance.get(`${FACTORY_API.LIST}?${params.toString()}`);
      setData({
        items: response.data.factories || [],
        total: response.data.total || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      message.error('Không thể tải danh sách nhà xưởng');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  // Effect để fetch data
  useEffect(() => {
    fetchFactories();
  }, [fetchFactories, queryParams, shouldRefetch]);

  // Xử lý tìm kiếm với debounce
  const handleSearch = debounce((values) => {
    setQueryParams(prev => ({
      ...prev,
      page: 1,
      search: values.search,
      status: values.status
    }));
  }, 500);

  // Reset form tìm kiếm
  const handleReset = () => {
    searchForm.resetFields();
    setQueryParams({
      page: 1,
      limit: 10,
      search: '',
      status: undefined
    });
  };

  const showModal = async (record = null) => {
    setEditingId(record?.FactoryID);
    if (record) {
      try {
        const response = await axiosInstance.get(FACTORY_API.DETAIL(record.FactoryID));
        form.setFieldsValue(response.data);
      } catch (error) {
        message.error('Không thể tải thông tin nhà xưởng');
      }
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingId) {
        // Cập nhật nhà xưởng
        await axiosInstance.put(FACTORY_API.UPDATE(editingId), values);
        message.success('Cập nhật nhà xưởng thành công');
      } else {
        // Thêm nhà xưởng mới
        await axiosInstance.post(FACTORY_API.CREATE, values);
        message.success('Thêm mới nhà xưởng thành công');
      }

      // Trigger fetch lại data
      setShouldRefetch(prev => prev + 1);
      setIsModalVisible(false);
      form.resetFields();
      setEditingId(null);
    } catch (error) {
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(FACTORY_API.DELETE(id));
      message.success('Xóa nhà xưởng thành công');
      // Trigger fetch lại data
      setShouldRefetch(prev => prev + 1);
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa nhà xưởng');
    }
  };

  const handleTableChange = (pagination) => {
    setQueryParams(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize
    }));
  };

  const columns = [
    {
      title: 'Mã nhà xưởng',
      dataIndex: 'FactoryID',
      key: 'FactoryID',
      width: 150,
    },
    {
      title: 'Vị trí nhà xưởng',
      dataIndex: 'FactoryName',
      key: 'FactoryName',
      width: 200,
    },
    {
      title: 'Tên nhà xưởng',
      dataIndex: 'Group',
      key: 'Group',
      width: 200,
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'Address',
      key: 'Address',
      width: 250,
    },
    {
      title: 'Diện tích (m²)',
      dataIndex: 'Area',
      key: 'Area',
      width: 150,
      align: 'right',
      render: (value) => value?.toLocaleString('vi-VN'),
    },
    {
      title: 'Giá (VNĐ)',
      dataIndex: 'Price',
      key: 'Price',
      width: 150,
      align: 'right',
      render: (value) => value?.toLocaleString('vi-VN'),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: 150,
      render: status => (
        <Tag icon={<StarOutlined />} color={status ? 'green' : 'red'} style={{ margin: 0 }}>
                 {status ? 'Hoạt động' : 'Dừng hoạt động'}
                </Tag>
      ),
    },
    
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <ActionButtons
            module="factories"
            showAdd={false}
            showView={false}
            onEdit={() => showModal(record)}
            onDelete={() => handleDelete(record.FactoryID)}
            record={record}
          />
          
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col>
              <Title level={2} style={{ margin: 0 }}>Quản lý nhà xưởng</Title>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
              >
                Thêm mới
              </Button>
            </Col>
          </Row>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="inline"
            onValuesChange={(_, values) => handleSearch(values)}
          >
            <Form.Item name="search">
              <Input
                placeholder="Tìm kiếm theo tên hoặc địa chỉ"
                prefix={<SearchOutlined />}
                allowClear
              />
            </Form.Item>
            <Form.Item name="status">
              <Select
                placeholder="Trạng thái"
                allowClear
                style={{ width: 200 }}
              >
                <Option value="true">Hoạt động</Option>
                <Option value="false">Ngừng hoạt động</Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Đặt lại
              </Button>
            </Form.Item>
          </Form>
        </div>

        <Table
          columns={columns}
          dataSource={data.items}
          rowKey="FactoryID"
          scroll={{ x: 1300 }}
          pagination={{
            current: data.currentPage,
            pageSize: queryParams.limit,
            total: data.total,
            showSizeChanger: true,
            showTotal: (total) => `Tổng số ${total} bản ghi`,
            onChange: (page, pageSize) => {
              setQueryParams(prev => ({
                ...prev,
                page: page,
                limit: pageSize
              }));
            }
          }}
          loading={loading}
        />

        <Modal
          title={editingId ? "Cập nhật nhà xưởng" : "Thêm mới nhà xưởng"}
          open={isModalVisible}
          onOk={handleSubmit}
          onCancel={handleCancel}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
          >
            <Form.Item
              name="FactoryID"
              label="Mã nhà xưởng"
              rules={[
                { required: true, message: 'Vui lòng nhập mã nhà xưởng' },
                { max: 20, message: 'Mã nhà xưởng không được vượt quá 20 ký tự' }
              ]}
            >
              <Input placeholder="Nhập mã nhà xưởng" disabled={!!editingId} />
            </Form.Item>

            <Form.Item
              name="FactoryName"
              label="Vị trí nhà xưởng"
              rules={[
                { required: true, message: 'Vui lòng nhập vị trí nhà xưởng' },
                { max: 100, message: 'Vị trí nhà xưởng không được vượt quá 100 ký tự' }
              ]}
            >
              <Input placeholder="Nhập vị trí nhà xưởng" />
            </Form.Item>

            <Form.Item
              name="Group"
              label="Tên nhà xưởng"
              rules={[
                { required: true, message: 'Vui lòng chọn tên nhà xưởng' }
              ]}
            >
              <Select>
                <Option value="NX 1-2">NX 1-2</Option>
                <Option value="NX 3-4">NX 3-4</Option>
                <Option value="NX 5-6">NX 5-6</Option>
                <Option value="NX 7-8">NX 7-8</Option>
                <Option value="NX 9-10">NX 9-10</Option>
                <Option value="NX 11-12">NX 11-12</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="Address"
              label="Địa chỉ"
              rules={[
                { required: true, message: 'Vui lòng nhập địa chỉ' },
                { max: 200, message: 'Địa chỉ không được vượt quá 200 ký tự' }
              ]}
            >
              <TextArea
                placeholder="Nhập địa chỉ"
                rows={3}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="Area"
                  label="Diện tích (m²)"
                  rules={[
                    { required: true, message: 'Vui lòng nhập diện tích' },
                    { type: 'number', min: 0, message: 'Diện tích phải lớn hơn 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Nhập diện tích"
                    min={0}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="Price"
                  label="Giá (VNĐ)"
                  rules={[
                    { required: true, message: 'Vui lòng nhập giá' },
                    { type: 'number', min: 0, message: 'Giá phải lớn hơn 0' }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="Nhập giá"
                    min={0}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="Description"
              label="Mô tả"
              rules={[
                { max: 500, message: 'Mô tả không được vượt quá 500 ký tự' }
              ]}
            >
              <TextArea
                placeholder="Nhập mô tả (nếu có)"
                rows={4}
              />
            </Form.Item>

            <Form.Item
              name="Status"
              label="Trạng thái"
              initialValue={true}
            >
              <Select>
                <Option value={true}>Hoạt động</Option>
                <Option value={false}>Ngừng hoạt động</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default FactoryManagement; 